import app from '../../app/app'
import Dropdown from '../generic/Dropdown'
import { A, all, reactive, Div, Span, or, and, not, add } from 'alkali'
import { usersnapOpen } from 'integrations/usersnap'
import * as searchLinkIcon from 'icons/app-window-search-text.svg'
import * as studyUpload from 'icons/common-file-text-upload.svg'
import * as collectionUpload from 'icons/database-upload.svg'
import { lastDocSearchQuery } from 'model/StudyCollection'
import Icon from 'components/Icon'
import { startExplanations } from '../entry/Explanation'
import * as userIcon from 'icons/single-neutral-circle.svg'
import * as settingsIcon from 'icons/cog.svg'
import * as privacyIcon from 'icons/touch-id-lock.svg'
import * as logoutIcon from 'icons/logout-1.svg'
import * as Pencil from 'icons/pencil-2.svg'
import * as home from 'icons/house-4.svg'
import AppSettingsOptions from './AppSettingsOptions'
import { SavedSearchesDialog } from './SavedSearches'
import DataImportTemplateDialog from './DataImportTemplateDialog'
import { track } from 'components/tracking/track'
import * as home from 'icons/house-4.svg'
import { adminFAQUser, studyFilter } from 'model/SearchQuery'


// User menu dropdown provides access to account and session management options

const getMenu = (productMenu: string, user: any) => {
	const topOptions = [
		{
			content: [Icon({ icon: settingsIcon }), Span('App Settings')],
			onclick: () => new AppSettingsOptions().show(),
		},
	]
	const baseOptions = [
		{
			classes: {
				disabled: reactive(user).to(u => !u?.Platforms?.some(p => p.Name == 'DOC Search') || studyFilter.importCollection),
			},
			content: A({
				content: [Icon('.doc-search-link', { icon: searchLinkIcon }), 'Open in DocSearch'],
				title: studyFilter.importCollection.to(collection => {
					return collection ? 'Collections cannot be opened in DocSearch' : 'Open search in DocSearch'
				}),
				href: lastDocSearchQuery.to(query => studyFilter.importCollection.to(async collection => {
					return !collection && (await app.user)?.Platforms?.some(p => p.Name == 'DOC Search') && query || undefined
				})),
				target: '_blank',
				onclick: event => {
					if (!(user.Platforms?.some(p => p.Name == 'DOC Search')))
						event.preventDefault()
				}
			}),
		},
		{
			content: [Icon('.doc-search-link', { icon: searchLinkIcon }), 'Saved DocSearches'],
			display: reactive(user).to(u => u?.Platforms?.some(p => p.Name == 'DOC Search')),
			onclick: (event) => {
				new SavedSearchesDialog().show(event)
			}
		},
		{
			classes: {
				disabled: reactive(user).to(u => !(u?.FeatureConfig?.['Upload Study']?.Enabled))
			},
			content: [Icon('.upload-study.clickable', { icon: studyUpload }), 'Upload new study'],
			title: 'Upload new study',
			onclick: event => {
				if (user?.FeatureConfig?.['Upload Study']?.Enabled)
					import('../extract/ExtractDialog').then(({ ExtractDialog }) =>
						new ExtractDialog({
							annotate: !event.shiftKey,
							debug: event.altKey,
							manage: true
						}).show())
			}
		},
		{
			classes: {
				disabled: reactive(user).to(u => !(u?.FeatureConfig?.['Collection']?.Enabled))
			},
			content: [Icon('.import-data.clickable', { icon: collectionUpload }), 'Collection Template'],
			title: 'Click to download template for extraction.',
			onclick: event => {
				if (user?.FeatureConfig?.['Collection']?.Enabled) {
					new DataImportTemplateDialog().show()
				}
			}
		},
	]
	const faqOptions = [{
		content: A({
			content: [Icon({ icon: privacyIcon }), 'Usage Policies'],
			href: 'https://confluence.doctorevidence.com/display/shareit/67502355/RWJ04f97a22588f4a518f42264a0d3a1095VVG',
			target: '_blank',
		})
	},
	{
		content: Span({
			content: [Icon({ icon: privacyIcon }), document.cookie.indexOf('track=no') > -1 ? 'Opt-in to tracking' : 'Opt-out of tracking'],
			title: 'Opt-in or out of the use of first-party cookie-based analytical tracking and statistic gathering',
			onclick() {
				let optingIn = document.cookie.indexOf('track=no') > -1
				document.cookie = 'track=' + (optingIn ? 'yes' : 'no')
			}
		})
	},
	]
	const daOptions = [
		{
			display: reactive(user).to(u => Boolean(u?.FeatureConfig?.['Bindings Admin']?.Enabled)),
			content: [Icon({ icon: Pencil }), Span('Terms Admin')],
			onclick: () => {
				if (user?.FeatureConfig?.['Bindings Admin']?.Enabled)
					window.open('/admin.html#terms', '_blank')
			},
		},
		{
			display: reactive(user).to(u => Boolean(u?.FeatureConfig?.Debug?.Enabled)),
			content: A({
				content: [Icon('.doc-search-link', { icon: searchLinkIcon }), 'Evidence Dashboard'],
				href: 'https://dashboard.doctorevidence.com/',
				onclick() {
					this.href = 'https://dashboard.doctorevidence.com/'
				},
				target: '_blank',
			})
		},
	]
	const bottomOptions = [
		{
			content: [Icon({ icon: home }), Span('Dr.Evidence home')],
			onclick: () => (location.href = 'https://launch.doctorevidence.com/#!/')
		},
		{
			content: [Icon({ icon: logoutIcon }), Span('Logout')],
			onclick: () => app.authentication.logout(),
		},
	]
	if (productMenu == 'daMenu') {
		return [
			...topOptions,
			...baseOptions,
			...faqOptions,
			...daOptions,
			...bottomOptions
		]
	} else if (productMenu == 'faqMenu'){
		const faqAnalysisPage = Boolean(document.querySelector('.faq-view'))
		if(faqAnalysisPage && adminFAQUser.valueOf()){
			return [
				...topOptions,
				...baseOptions,
				...faqOptions,
				...daOptions,
				...bottomOptions
			]
		} else if (!adminFAQUser.valueOf() && faqAnalysisPage){
			return [
				...topOptions,
				...faqOptions,
				...bottomOptions
			]
		}
		return [
			...faqOptions,
			...bottomOptions
		]
	}
}

export default class UserMenu extends Div('#nav-usermenu.nav-tab.nav-dropdown', {
	title: 'User options, change password, app settings, and logout'
}) {
	onclick = (e) => {
		if (app.authentication.isAuth())
			this.dropdown.show(e)
		else
			app.authentication.showLogin()
		track({ event: 'user-menu' })
	}
	ready({ mode }) {
		this.dropdown = new Dropdown({
			items: all([app.userVar.property('allowImpersonate'), app.impersdUser]).to(([canImpersonate, impersonating]) =>
				((canImpersonate || impersonating) ? [
					{
						content: [Span('.fa.fa-user-secret'), impersonating ? Span('Stop impersonating') : Span('Impersonate user...')],
						onclick: () => {
							if (impersonating) {
								app.impersonateEnd()
							} else {
								new UserSelectionDialog({
									onUserSelect: (userToImpersonate) => app.impersonate(userToImpersonate.Id)
								}).show()
							}
						}
					}
				] : []).concat(
					isFAQDashboard ? getMenu('faqMenu', app.user) : getMenu('daMenu', app.user)
				))
		})
		const displayName = profile => {
			if (profile?.FirstName)
				return profile?.FirstName.length > 11 ? null : profile?.FirstName + ' '
			else if (profile?.Name?.split('@')?.[0])
				return profile?.Name?.split('@')?.[0].length > 11 ? null : profile?.Name?.split('@')?.[0] + ' '
			else
				return 'Login '
		}
		this.append([
			Span('.username', {
				content: app.authdUser.to(displayName),
			}),
			Icon({ icon: userIcon }),
		])
	}
}
